import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from 'Context';
import { VideoUploadModal } from 'Components/VideoUploadModal';
import { PhotoUploadModal } from 'Components/PhotoUploadModal';
import CloudUploadModal from 'Components/UploadDropdown/CloudUploadModal';
import styles from './QuickUserUploadPage.module.css';

function QuickUserUploadPage() {
  const navigate = useNavigate();
  const {
    userDetails,
    setAppSnackbar,
  } = useContext(AppContext);

  const quickUserEmail = sessionStorage.getItem('quickUserEmail') || userDetails?.email || 'Their';

  // Video upload modal state
  const [videoModalOpen, setVideoModalOpen] = useState(false);

  // Photo/Album upload modal state
  const [photoModalOpen, setPhotoModalOpen] = useState(false);

  // Cloud upload state
  const [cloudModalOpen, setCloudModalOpen] = useState(false);
  const [selectedCloudService, setSelectedCloudService] = useState(null);

  // Prevent navigating back to this page once left
  useEffect(() => {
    const alreadyVisited = sessionStorage.getItem('quickUploadVisited');
    if (alreadyVisited) {
      sessionStorage.removeItem('quickUploadVisited');
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  // Re-open cloud modal after OAuth redirect (same as UploadDropdown)
  useEffect(() => {
    const autoOpenService = sessionStorage.getItem('autoOpenCloudModal');
    if (autoOpenService) {
      sessionStorage.removeItem('autoOpenCloudModal');
      setSelectedCloudService(autoOpenService);
      setCloudModalOpen(true);
    }
  }, []);

  const leaveUploadPage = (path) => {
    sessionStorage.setItem('quickUploadVisited', 'true');
    navigate(path || '/dashboard', { replace: true });
  };

  const handleChooseLocalFiles = () => {
    sessionStorage.setItem('quickUploadVisited', 'true');
    setVideoModalOpen(true);
  };

  const handleCloudUpload = (service) => {
    sessionStorage.setItem('quickUploadVisited', 'true');
    setSelectedCloudService(service);
    setCloudModalOpen(true);
  };

  const handleCloudModalClose = () => {
    setCloudModalOpen(false);
    setSelectedCloudService(null);
  };

  const handleCloudFilesSelected = (files, service) => {
    if (files.length === 0) return;
    // Store selected cloud files for UploadDropdown to pick up on dashboard
    sessionStorage.setItem('pendingCloudFiles', JSON.stringify({ files, service }));
    handleCloudModalClose();
    leaveUploadPage();
  };

  return (
    <div className={styles.page}>
      <div className={styles.contentContainer}>
        <h2 className={styles.heading}>{`Add Media To ${quickUserEmail}'s Projector`}</h2>

        <div className={styles.flipCardContainer}>
          <div className={styles.flipCardFront}>
            <div className={styles.uploadOptionsGrid}>
        {/* Box 1: Choose Local Files */}
        <div
          aria-hidden="true"
          className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay1}`}
          onClick={handleChooseLocalFiles}
        >
          <div className={styles.uploadOptionIcon}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="2" y="4" width="20" height="16" rx="2" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M10 8.5L16 12L10 15.5V8.5Z" fill="white" />
            </svg>
          </div>
          <span className={styles.uploadOptionText}>Upload Videos</span>
        </div>

        {/* Box 2: Upload Albums */}
        <div
          aria-hidden="true"
          className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay2}`}
          onClick={() => { sessionStorage.setItem('quickUploadVisited', 'true'); setPhotoModalOpen(true); }}
        >
          <div className={styles.uploadOptionIcon}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="8.5" cy="8.5" r="1.5" stroke="white" strokeWidth="2" />
              <polyline points="21 15 16 10 5 21" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <span className={styles.uploadOptionText}>Upload Albums</span>
        </div>

        {/* Box 3: Apple Photos - commented out
        <div
          aria-hidden="true"
          className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay3}`}
          onClick={() => { }}
        >
          <div className={styles.uploadOptionIcon}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.05 20.28c-.98.95-2.05.79-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" fill="#999" />
            </svg>
          </div>
          <span className={styles.uploadOptionText}>Apple Photos</span>
        </div>
        */}

        {/* Box 4: Google Drive */}
        <div
          aria-hidden="true"
          className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay3}`}
          onClick={() => handleCloudUpload('googledrive')}
        >
          <div className={styles.uploadOptionIcon}>
            <svg width="32" height="32" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
              <path d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5l5.4 9.35z" fill="#0066DA" />
              <path d="M43.65 25L29.9 1.2c-1.35.8-2.5 1.9-3.3 3.3L1.2 52.35c-.8 1.4-1.2 2.95-1.2 4.5h27.5L43.65 25z" fill="#00AC47" />
              <path d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75L86.1 57c.8-1.4 1.2-2.95 1.2-4.5H59.85L73.55 76.8z" fill="#EA4335" />
              <path d="M43.65 25L57.4 1.2c-1.35-.8-2.9-1.2-4.5-1.2H34.35c-1.6 0-3.15.45-4.5 1.2L43.65 25z" fill="#00832D" />
              <path d="M59.85 53H27.5l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.55c1.6 0 3.15-.45 4.5-1.2L59.85 53z" fill="#2684FC" />
              <path d="M73.4 26.5l-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25l16.2 28h27.5c0-1.55-.4-3.1-1.2-4.5l-12.75-22z" fill="#FFBA00" />
            </svg>
          </div>
          <span className={styles.uploadOptionText}>Google Drive</span>
        </div>

        {/* Box 5: Dropbox */}
        <div
          aria-hidden="true"
          className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay4}`}
          onClick={() => handleCloudUpload('dropbox')}
        >
          <div className={styles.uploadOptionIcon}>
            <svg width="40" height="40" viewBox="0 0 43 40" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.5 0L0 8.1l8.6 6.9 12.5-7.8L12.5 0zM0 21.9l12.5 8.1 8.6-7.1-12.5-7.8L0 21.9zM21.1 22.9l8.6 7.1 12.5-8.1-8.6-6.8-12.5 7.8zM42.2 8.1L29.7 0l-8.6 7.2 12.5 7.8 8.6-6.9zM21.2 24.6l-8.6 7.1-4 -2.6v2.9l12.6 7.6 12.6-7.6v-2.9l-4 2.6-8.6-7.1z" fill="#0061ff" />
            </svg>
          </div>
          <span className={styles.uploadOptionText}>Dropbox</span>
        </div>

        {/* Box 6: Google Photos - commented out
        <div
          aria-hidden="true"
          className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay6}`}
          onClick={() => handleCloudUpload('googlephotos')}
        >
          <div className={styles.uploadOptionIcon}>
            <svg width="40" height="40" viewBox="0 0 122.88 122.88" xmlns="http://www.w3.org/2000/svg">
              <path d="M30.72,27.97c16.95,0,30.72,13.77,30.72,30.72v2.83H2.83c-1.52,0-2.75-1.23-2.83-2.83 C0,41.73,13.77,27.97,30.72,27.97L30.72,27.97z" fill="#F6B704" />
              <path d="M94.91,30.72c0,16.95-13.77,30.72-30.72,30.72l-2.75,0V2.83C61.44,1.3,62.67,0,64.19,0 C81.22,0,94.91,13.77,94.91,30.72L94.91,30.72z" fill="#E54335" />
              <path d="M92.16,94.99c-16.95,0-30.72-13.77-30.72-30.72v-2.83h58.61c1.52,0,2.83,1.23,2.83,2.75 C122.88,81.22,109.11,94.99,92.16,94.99L92.16,94.99L92.16,94.99z" fill="#4280EF" />
              <path d="M27.97,92.16c0-16.95,13.77-30.72,30.72-30.72h2.75v58.61c0,1.52-1.23,2.75-2.83,2.83 C41.66,122.88,27.97,109.11,27.97,92.16L27.97,92.16L27.97,92.16z" fill="#34A353" />
            </svg>
          </div>
          <span className={styles.uploadOptionText}>Google Photos</span>
        </div>
        */}
            </div>
          </div>
        </div>

        {/* Skip - No Media To Upload box (same style as gift flow) */}
        <div
          aria-hidden="true"
          className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay5}`}
          onClick={() => leaveUploadPage()}
          style={{ marginTop: '20px', width: '100%', maxWidth: '400px', height: 'auto', padding: '20px 16px' }}
        >
          <span className={styles.uploadOptionText}>No Media To Upload</span>
          <span style={{ color: '#ccc', fontSize: '0.75rem', fontFamily: "'Montserrat', sans-serif", fontWeight: 500, textAlign: 'center', lineHeight: 1.4, marginTop: '8px' }}>
            It&apos;s completely ok! You can add as much media as you would like later!
          </span>
        </div>
      </div>

      {/* Video Upload Modal */}
      <VideoUploadModal
        open={videoModalOpen}
        onClose={() => setVideoModalOpen(false)}
        userDataProp={{
          subscription: userDetails?.isSubscription || null,
          totalContentUploaded: userDetails?.totalContentUploaded || 0
        }}
      />

      {/* Photo/Album Upload Modal */}
      <PhotoUploadModal
        open={photoModalOpen}
        onClose={() => setPhotoModalOpen(false)}
        userData={{
          subscription: userDetails?.isSubscription || null,
          totalContentUploaded: userDetails?.totalContentUploaded || 0
        }}
      />

      {/* Cloud Upload Modal (Dropbox / Google Drive / Google Photos) */}
      <CloudUploadModal
        open={cloudModalOpen}
        onClose={handleCloudModalClose}
        service={selectedCloudService}
        onFilesSelected={handleCloudFilesSelected}
        onShowMessage={(message) => setAppSnackbar({
          isVisible: true,
          message,
          type: 'default',
        })}
      />
    </div>
  );
}

export default QuickUserUploadPage;
